<template>
  <section class="testimonials lp-section">
    <div class="lp-container">
      <header class="t-head">
        <div class="lp-eyebrow"><span class="lp-eyebrow__dot"></span>{{ $t('nav.testimonials') }}</div>
        <h2 class="lp-h2 lp-balance">{{ $t('testimonials.title') }}</h2>
        <p class="lp-lead t-sub lp-balance">{{ $t('testimonials.subtitle') }}</p>
      </header>

      <div class="t-grid">
        <figure
          v-for="(t, i) in testimonials"
          :key="i"
          class="t-card"
        >
          <div class="t-stars" :aria-label="`${rating(i)} / 5`">
            <span class="star" v-for="n in 5" :key="n">
              <span class="star-bg"><l-icon size="16">mdi-star</l-icon></span>
              <span class="star-fg" :style="{ width: starWidth(rating(i), n) }"><l-icon size="16">mdi-star</l-icon></span>
            </span>
          </div>
          <blockquote class="t-quote">{{ t.quote }}</blockquote>
          <figcaption class="t-author">
            <span class="t-avatar">{{ initials(t.name) }}</span>
            <span class="t-meta">
              <span class="t-name">{{ t.name }}</span>
              <span class="t-role">{{ t.role }}</span>
            </span>
          </figcaption>
        </figure>
      </div>

      <div class="t-stats">
        <div v-for="(stat, i) in stats" :key="i" class="t-stat">
          <div class="t-stat__value">{{ stat.value }}</div>
          <div class="t-stat__label">{{ stat.label }}</div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'TestimonialsSection',
  data() {
    return {
      ratings: [5, 5, 4.5, 5, 4, 4.5],
    };
  },
  computed: {
    testimonials() {
      return this.$t('testimonials.items');
    },
    stats() {
      return this.$t('testimonials.stats');
    },
  },
  methods: {
    rating(i) {
      return this.ratings[i % this.ratings.length];
    },
    starWidth(r, n) {
      const fraction = Math.max(0, Math.min(1, r - (n - 1)));
      return `${fraction * 100}%`;
    },
    initials(name) {
      return (name || '')
        .split(' ')
        .map((w) => w[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },
  },
};
</script>

<style scoped>
.testimonials { background: var(--lp-bg-subtle); }

.t-head {
  text-align: center;
  max-width: 640px;
  margin: 0 auto 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
}

.t-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.t-card {
  margin: 0;
  background: var(--lp-surface);
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius-lg);
  padding: 28px;
  display: flex;
  flex-direction: column;
  box-shadow: var(--lp-shadow-sm);
}

.t-stars {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-bottom: 18px;
}
.star {
  position: relative;
  width: 16px;
  height: 16px;
  line-height: 0;
  flex: 0 0 auto;
}
.star-bg,
.star-fg {
  position: absolute;
  top: 0;
  left: 0;
  height: 16px;
  display: inline-flex;
}
.star-bg { color: #d6d9de; }
.star-fg {
  color: #f5a623;
  overflow: hidden;
  white-space: nowrap;
}
.t-stars >>> .star-bg svg { fill: none; }
.t-stars >>> .star-fg svg { fill: currentColor; }
.t-rating-num {
  margin-left: 7px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--lp-muted);
}

.t-quote {
  margin: 0 0 24px;
  font-size: 1.0625rem;
  line-height: 1.6;
  color: var(--lp-ink);
  font-weight: 400;
  letter-spacing: -0.01em;
  flex: 1;
}

.t-author {
  display: flex;
  align-items: center;
  gap: 13px;
  padding-top: 20px;
  border-top: 1px solid var(--lp-line);
}
.t-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--lp-bg-subtle);
  border: 1px solid var(--lp-line);
  color: var(--lp-ink-2);
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 0.85rem;
  flex: 0 0 auto;
}
.t-meta { display: flex; flex-direction: column; }
.t-name { font-weight: 600; font-size: 0.95rem; color: var(--lp-ink); }
.t-role { font-size: 0.825rem; color: var(--lp-muted); }

/* Stats */
.t-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 64px;
  padding-top: 56px;
  border-top: 1px solid var(--lp-line);
}
.t-stat { text-align: center; }
.t-stat__value {
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2.25rem, 4vw, 3rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--lp-ink);
  line-height: 1;
  margin-bottom: 10px;
}
.t-stat__label {
  font-size: 0.95rem;
  color: var(--lp-muted);
  max-width: 22ch;
  margin: 0 auto;
}

@media (max-width: 959px) {
  .t-grid { grid-template-columns: 1fr; max-width: 540px; margin: 0 auto; }
  .t-stats { grid-template-columns: 1fr; gap: 36px; }
}
</style>
